/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, type ReactNode } from 'react';
import { InfoIconButton } from './InfoIconButton';
import { AreaIntroPanel } from './AreaIntroPanel';

export interface AreaIntroConfig {
  /** Accessible name / tooltip for the info button. */
  label: string;
  body: string;
  /** Prefix for toggle/panel test ids, e.g. `clients` → `clients-area-intro-toggle`. */
  testIdPrefix?: string;
}

export interface AreaIntroToggleResult {
  accessory: ReactNode;
  panel: ReactNode;
  open: boolean;
}

/**
 * Owns open state for the info (i) control next to an area title.
 *
 * @param config - Localized intro copy, or undefined to disable.
 * @returns Title accessory button and expandable panel.
 */
export function useAreaIntroToggle(
  config?: AreaIntroConfig | null,
): AreaIntroToggleResult {
  const [open, setOpen] = useState(false);
  if (!config?.label || !config?.body) {
    return { accessory: null, panel: null, open: false };
  }
  const prefix = config.testIdPrefix ?? 'area';
  return {
    open,
    accessory: (
      <InfoIconButton
        data-testid={`${prefix}-area-intro-toggle`}
        label={config.label}
        title={config.label}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      />
    ),
    panel: open ? (
      <AreaIntroPanel body={config.body} testId={`${prefix}-area-intro`} />
    ) : null,
  };
}
